import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Chip, Tab, Tabs,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  SwapHoriz as TransferIcon, Inventory as StockIcon,
} from '@mui/icons-material';
import { warehousesApi } from '../api/warehouses.api';
import { productsApi } from '../api/products.api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Warehouses: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [stock, setStock] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');

  // Transfer form
  const [fromWarehouseId, setFromWarehouseId] = useState(0);
  const [toWarehouseId, setToWarehouseId] = useState(0);
  const [transferProductId, setTransferProductId] = useState(0);
  const [transferQty, setTransferQty] = useState(0);
  const [transferReason, setTransferReason] = useState('');

  const load = async () => {
    try {
      const [whRes, trRes, prodRes] = await Promise.all([
        warehousesApi.getAll(),
        warehousesApi.getTransfers(),
        productsApi.getAll(),
      ]);
      setWarehouses(whRes.data);
      setTransfers(trRes.data);
      setProducts(prodRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setName(''); setAddress(''); setEditId(null); };

  const handleSave = async () => {
    try {
      if (isEditing && editId) {
        await warehousesApi.update(editId, { name, address });
      } else {
        await warehousesApi.create({ name, address });
      }
      setModalOpen(false); resetForm(); load();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async () => {
    try {
      await warehousesApi.delete(deleteId);
      setDeleteDialogOpen(false); load();
    } catch (err) { console.error(err); }
  };

  const handleViewStock = async (wh: any) => {
    try {
      const res = await warehousesApi.getStock(wh.id);
      setStock(res.data);
      setSelectedWarehouse(wh);
      setStockModalOpen(true);
    } catch (err) { console.error(err); }
  };

  const handleTransfer = async () => {
    try {
      await warehousesApi.createTransfer({
        fromWarehouseId, toWarehouseId,
        productId: transferProductId,
        quantity: transferQty,
        reason: transferReason,
      });
      setTransferModalOpen(false);
      setFromWarehouseId(0); setToWarehouseId(0);
      setTransferProductId(0); setTransferQty(0); setTransferReason('');
      load();
    } catch (err) { console.error(err); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Bodegas</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<TransferIcon />}
            onClick={() => setTransferModalOpen(true)}>
            Nueva Transferencia
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { resetForm(); setIsEditing(false); setModalOpen(true); }}>
            Nueva Bodega
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Bodegas" />
        <Tab label="Transferencias" />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Direccion</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouses.map((wh) => (
                <TableRow key={wh.id} hover>
                  <TableCell>{wh.id}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{wh.name}</TableCell>
                  <TableCell>{wh.address || '-'}</TableCell>
                  <TableCell>
                    <Chip label={wh.isActive ? 'Activa' : 'Inactiva'} size="small"
                      color={wh.isActive ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="info" onClick={() => handleViewStock(wh)}>
                      <StockIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="primary" onClick={() => {
                      setEditId(wh.id); setName(wh.name); setAddress(wh.address || '');
                      setIsEditing(true); setModalOpen(true);
                    }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => {
                      setDeleteId(wh.id); setDeleteDialogOpen(true);
                    }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Fecha</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map((t: any) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.product?.name}</TableCell>
                  <TableCell>{t.fromWarehouse?.name}</TableCell>
                  <TableCell>{t.toWarehouse?.name}</TableCell>
                  <TableCell align="right">{t.quantity}</TableCell>
                  <TableCell>{t.reason || '-'}</TableCell>
                  <TableCell>{t.user?.fullName}</TableCell>
                  <TableCell>{new Date(t.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal Bodega */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Bodega' : 'Nueva Bodega'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField fullWidth label="Nombre" value={name}
                onChange={(e) => setName(e.target.value)} required />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Direccion" value={address}
                onChange={(e) => setAddress(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Transferencia */}
      <Dialog open={transferModalOpen} onClose={() => setTransferModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Transferencia</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Bodega Origen</InputLabel>
                <Select value={fromWarehouseId} label="Bodega Origen"
                  onChange={(e) => setFromWarehouseId(Number(e.target.value))}>
                  {warehouses.map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Bodega Destino</InputLabel>
                <Select value={toWarehouseId} label="Bodega Destino"
                  onChange={(e) => setToWarehouseId(Number(e.target.value))}>
                  {warehouses.filter(w => w.id !== fromWarehouseId).map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Producto</InputLabel>
                <Select value={transferProductId} label="Producto"
                  onChange={(e) => setTransferProductId(Number(e.target.value))}>
                  {products.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Cantidad" type="number" value={transferQty}
                onChange={(e) => setTransferQty(Number(e.target.value))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Motivo" value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTransferModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleTransfer}>Transferir</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Stock */}
      <Dialog open={stockModalOpen} onClose={() => setStockModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Stock de {selectedWarehouse?.name}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stock.map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.product?.name}</TableCell>
                    <TableCell>{s.product?.category?.name || '-'}</TableCell>
                    <TableCell align="right">{s.quantity}</TableCell>
                  </TableRow>
                ))}
                {stock.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">Sin stock registrado</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={deleteDialogOpen} title="Eliminar Bodega"
        message="¿Estas seguro de eliminar esta bodega?"
        onConfirm={handleDelete} onCancel={() => setDeleteDialogOpen(false)} />
    </Box>
  );
};

export default Warehouses;
